// src/services/api.js
//
// API Service — capa de conexión al backend .NET de ET EducaT.
// Sigue el mismo patrón que news_frontend/src/services/api.js:
//   - Auth real vía Ecolab (JWT compartido).
//   - USE_MOCK flag para poder seguir usando datos de demo mientras
//     se termina de conectar cada pantalla.
//   - Refresh automático de JWT ante 401.
//   - Adaptadores que traducen el shape del backend (español,
//     PascalCase→camelCase via JSON serializer) al shape que ya
//     consumen los componentes actuales del frontend.
//
// Variables de entorno esperadas (.env / .env.local):
//   REACT_APP_API_URL          Base del backend .NET, ej: http://localhost:5000
//   REACT_APP_USE_MOCK         'false' para hablar con el backend real
//   REACT_APP_ECOLAB_API_URL   Base de Ecolab (login/refresh) — solo modo Fenagro
//   REACT_APP_STORAGE_BUCKET   Bucket de Firebase Storage (gs:// → https://)
//   REACT_APP_MODO_OPERACION   'Fenagro' (default) o 'Redeprog' — decide si login/refresh
//                              van contra Ecolab o contra EducaT local.

const API_BASE_URL   = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/v1/educat';
const USE_MOCK        = process.env.REACT_APP_USE_MOCK !== 'false';
const ECOLAB_API_URL  = process.env.REACT_APP_ECOLAB_API_URL ||
  'https://ecolab-backend-723916953839.us-central1.run.app';
const STORAGE_BUCKET  = process.env.REACT_APP_STORAGE_BUCKET || 'educat-media.firebasestorage.app';

// Fase 5 Redeprog SaaS: modo de operación del backend. Determina contra qué
// endpoints se hace login/refresh. En modo Fenagro (default) sigue todo
// igual: auth vía Ecolab. En modo Redeprog: auth local contra el propio
// EducaT (endpoints /api/v1/educat/auth/*).
export const MODO_OPERACION = process.env.REACT_APP_MODO_OPERACION || 'Fenagro';
export const IS_REDEPROG = MODO_OPERACION === 'Redeprog';

const TOKEN_KEY   = 'educatToken';
const REFRESH_KEY = 'educatRefreshToken';
const USER_KEY    = 'educatUser';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ═══════════════════════════════════════════════════════════════════════
//  Refresh automático de JWT ante 401
// ═══════════════════════════════════════════════════════════════════════
let _refreshInFlight = null;

const _tryRefreshToken = async () => {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return false;
  try {
    // Fase 5 Redeprog SaaS: en modo Redeprog el refresh va contra EducaT
    // local; en Fenagro sigue yendo a Ecolab como siempre.
    const refreshUrl = IS_REDEPROG
      ? `${API_BASE_URL}/auth/refresh`
      : `${ECOLAB_API_URL}/api/ecolab/auth/refresh`;
    const resp = await fetch(refreshUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!resp.ok) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(USER_KEY);
      return false;
    }
    const data = await resp.json();
    const newToken   = data.token || data.accessToken || data.access_token;
    const newRefresh = data.refreshToken || data.refresh_token;
    if (!newToken) return false;
    localStorage.setItem(TOKEN_KEY, newToken);
    if (newRefresh) localStorage.setItem(REFRESH_KEY, newRefresh);
    return true;
  } catch {
    return false;
  }
};

const ensureFreshToken = () => {
  if (!_refreshInFlight) {
    _refreshInFlight = _tryRefreshToken().finally(() => { _refreshInFlight = null; });
  }
  return _refreshInFlight;
};

// ═══════════════════════════════════════════════════════════════════════
//  HTTP helper
// ═══════════════════════════════════════════════════════════════════════
const _doFetch = async (endpoint, options) => {
  const url   = `${API_BASE_URL}${endpoint}`;
  const token = localStorage.getItem(TOKEN_KEY);
  const isFormData = options?.body instanceof FormData;
  const defaultOptions = {
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };
  return fetch(url, { ...defaultOptions, ...options });
};

// Cuando el backend rechaza la sesión de forma no recuperable (no
// autorizado para esta app, expirada, o inválida) y ya se intentó
// refrescar el token sin éxito, se dispara este evento global UNA
// SOLA VEZ desde acá -- en vez de que cada página tenga que manejar
// el 401 por su cuenta con su propio try/catch. Un componente montado
// en la raíz de la app (GlobalAuthErrorModal.js) lo escucha y bloquea
// toda la pantalla hasta que el usuario reaccione (cerrar sesión).
const dispararBloqueoDeSesion = (codigo, mensaje) => {
  window.dispatchEvent(new CustomEvent('educat:auth-blocked', { detail: { codigo, mensaje } }));
};

const fetchApi = async (endpoint, options = {}) => {
  // Se guarda ANTES de la llamada -- necesario para distinguir, si
  // llega un 401, entre "nunca hubo sesión" (visitante anónimo, normal
  // en páginas públicas) y "sí había sesión y se rompió" (justifica
  // bloquear la app). El interceptor de abajo solo debe actuar en el
  // segundo caso.
  const habiaTokenAntes = !!localStorage.getItem(TOKEN_KEY);

  let response = await _doFetch(endpoint, options);

  if (response.status === 401 && localStorage.getItem(TOKEN_KEY) && localStorage.getItem(REFRESH_KEY)) {
    const refreshed = await ensureFreshToken();
    if (refreshed) response = await _doFetch(endpoint, options);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const base = errorData.mensaje || errorData.error || errorData.message || `API Error: ${response.status}`;
    // Si el backend adjunta un 'detalle' (p.ej. en errores de storage —
    // bucket no configurado, credenciales incorrectas, permiso denegado —
    // o cualquier otro endpoint que devuelva { mensaje, detalle }), lo
    // sumamos al mensaje para que el usuario vea la causa real sin
    // tener que abrir la pestaña Network.
    const mensaje = errorData.detalle ? `${base} — ${errorData.detalle}` : base;

    // 401 que sobrevive al intento de refresh, Y que además ocurrió
    // teniendo una sesión iniciada, es sesión genuinamente rota (no
    // autorizado para esta app, expirada, inválida) -- eso sí bloquea
    // la app entera, porque no es algo que la página pueda resolver
    // reintentando.
    //
    // Si NUNCA hubo token (visitante anónimo navegando una página
    // pública que de paso intenta una llamada opcional autenticada,
    // ej. "¿estoy inscrito en este curso?"), el 401 es normal y
    // esperado -- se deja pasar como error normal para que el propio
    // caller lo maneje (muchas llamadas ya tienen su .catch(() => {})
    // puesto justo para esto). Bloquear la app entera ahí sería un
    // error: no hay ninguna sesión que "cerrar".
    if (response.status === 401 && habiaTokenAntes) {
      dispararBloqueoDeSesion(errorData.codigo, mensaje);
      // No se lanza el error normal: si se hiciera throw acá, cada
      // página que no tenga su propio try/catch dejaría una promesa
      // rechazada sin capturar, y React mostraría el overlay rojo de
      // "Uncaught runtime errors" por ENCIMA del modal de bloqueo ya
      // controlado. Como la pantalla completa ya queda tapada por el
      // modal, no importa que esta llamada puntual se quede sin
      // resolver -- nunca se ve, porque nada más puede interactuar
      // con la página de todos modos.
      return new Promise(() => {});
    }

    // status viaja en el error para que cada pagina distinga 401/403
    // (no autorizado) de 404 (no existe) y de fallas del servidor.
    const err = new Error(mensaje);
    err.status = response.status;
    throw err;
  }

  if (response.status === 204) return null;
  return response.json();
};

const qs = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') query.set(k, v);
  });
  const s = query.toString();
  return s ? `?${s}` : '';
};

// ═══════════════════════════════════════════════════════════════════════
//  Adaptadores gs:// → https://
// ═══════════════════════════════════════════════════════════════════════
const gsToHttps = (gsUri) => {
  if (!gsUri) return null;
  if (!gsUri.startsWith('gs://')) return gsUri;
  const parts = gsUri.substring(5).split('/');
  const bucket = parts.shift();
  const path = parts.join('/');
  return `https://storage.googleapis.com/${bucket}/${path}`;
};

// ═══════════════════════════════════════════════════════════════════════
//  AUTENTICACIÓN (Ecolab)
// ═══════════════════════════════════════════════════════════════════════
//
// El login real vive en AppContext (AuthProvider). Aquí se exponen
// wrappers de compatibilidad y helpers que dependen del backend .NET
// ya autenticado.
export const authService = {
  login: async (email, password, turnstileToken) => {
    if (USE_MOCK) {
      await delay(400);
      return { success: false, error: 'USE_MOCK activo — usa el AuthProvider real o desactiva el mock.' };
    }
    // Fase 5 Redeprog SaaS: en modo Redeprog el login va contra EducaT
    // local (sin Turnstile, sin dependencia de Ecolab); en Fenagro sigue
    // contra Ecolab como siempre.
    const loginUrl = IS_REDEPROG
      ? `${API_BASE_URL}/auth/login`
      : `${ECOLAB_API_URL}/api/ecolab/auth/login`;
    const body = IS_REDEPROG
      ? { email, password }
      : { email, password, turnstileToken };

    const resp = await fetch(loginUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.mensaje || err.message || `Error ${resp.status}`);
    }
    const data = await resp.json();
    const accessToken  = data.token || data.accessToken;
    const refreshToken = data.refreshToken;
    if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    return { success: true, token: accessToken, usuario: data.usuario };
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },

  registrarFcmToken: async (fcmToken) => {
    if (USE_MOCK) return null;
    return fetchApi('/me/fcm-token', { method: 'POST', body: JSON.stringify({ token: fcmToken }) });
  },

  // Fase 5 Redeprog SaaS: forgot/reset password locales (solo modo Redeprog).
  // En modo Fenagro estos flujos los maneja Ecolab con su propio UI.
  forgotPassword: async (email) => {
    const resp = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return await resp.json();
  },

  resetPassword: async ({ email, token, password }) => {
    const resp = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, token, password }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.mensaje || 'No se pudo restablecer');
    return data;
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  ME — perfil, instituciones, cursos, notificaciones del usuario actual
// ═══════════════════════════════════════════════════════════════════════
export const meService = {
  getPerfil: async () => {
    if (USE_MOCK) return null;
    const p = await fetchApi('/me');
    return {
      id: p.id, email: p.email, name: [p.nombre, p.apellido].filter(Boolean).join(' '),
      avatar: gsToHttps(p.fotoUrl), phone: p.celular, role: mapRolBackendToFrontend(p.rol),
      municipio: p.municipioNombre, departamento: p.departamentoNombre,
    };
  },

  updatePerfil: async (data) => fetchApi('/me', {
    method: 'PUT',
    body: JSON.stringify({
      nombre: data.name, apellido: data.lastName, celular: data.phone,
      direccion: data.address, municipioId: data.municipalityId, municipioNombre: data.municipalityName,
      departamentoId: data.departmentId, departamentoNombre: data.departmentName,
      paisId: data.countryId, paisNombre: data.countryName,
      tipoDocumento: data.documentType, numeroDocumento: data.documentNumber,
      fechaNacimiento: data.birthDate, genero: data.gender,
    }),
  }),

  getMisInstituciones: async () => {
    if (USE_MOCK) return [];
    const list = await fetchApi('/me/instituciones');
    return list.map((i) => ({
      id: i.institucionId, name: i.nombre, shortName: i.nombreCorto,
      logo: gsToHttps(i.logoUrl), type: i.tipo, municipality: i.municipioNombre,
      role: i.tipoVinculo,
    }));
  },

  getMisMatriculasInstitucionales: async () => {
    if (USE_MOCK) return [];
    const list = await fetchApi('/me/matriculas-institucionales');
    return list.map((m) => ({
      id: m.matriculaInstId, institutionId: m.institucionId, institutionName: m.institucionNombre,
      enrollmentNumber: m.numeroMatricula, status: m.estado,
    }));
  },

  getMisCursos: async () => {
    if (USE_MOCK) return [];
    const list = await fetchApi('/me/cursos');
    return list.map((c) => ({
      enrollmentId: c.matriculaId, id: c.cursoId, slug: c.cursoSlug, title: c.cursoTitulo,
      image: gsToHttps(c.cursoImagenUrl), instructorId: c.instructorId, instructor: c.instructorNombre,
      progress: c.progreso, finalGrade: c.calificacionFinal, enrolledAt: c.fechaMatricula,
      lastViewedContentId: c.ultimoContenidoVistoId,
    }));
  },

  getNotificaciones: async (soloNoLeidas = false) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/me/notificaciones${qs({ soloNoLeidas })}`);
    return list.map(mapNotificacion);
  },

  marcarNotificacionesLeidas: async (ids = null) =>
    fetchApi('/me/notificaciones/leer', { method: 'POST', body: JSON.stringify(ids) }),
};

const mapRolBackendToFrontend = (rol) => {
  const map = { Administrador: 'admin', Instructor: 'instructor', Estudiante: 'student' };
  return map[rol] || 'student';
};

const mapNotificacion = (n) => ({
  id: n.id, type: n.tipo, title: n.titulo, message: n.mensaje,
  url: n.urlDestino, read: n.leida, createdAt: n.fechaCreacion,
});

// ═══════════════════════════════════════════════════════════════════════
//  INSTITUCIONES
// ═══════════════════════════════════════════════════════════════════════
const mapInstitucion = (i) => ({
  id: i.id, name: i.nombre, shortName: i.nombreCorto, nit: i.nit, type: i.tipo,
  logo: gsToHttps(i.logoUrl), municipality: i.municipioNombre, department: i.departamentoNombre,
  active: i.activa, verified: i.verificada,
});

const mapInstitucionDetalle = (i) => ({
  ...mapInstitucion(i),
  ecolabEntidadId: i.ecolabEntidadId, ecolabTipoEntidad: i.ecolabTipoEntidad,
  countryId: i.paisId, countryName: i.paisNombre,
  departmentId: i.departamentoId, municipalityId: i.municipioId,
  address: i.direccion, latitude: i.latitud, longitude: i.longitud,
  phone: i.telefono, email: i.emailInstitucional, website: i.sitioWeb,
  createdAt: i.fechaCreacion,
});

export const institucionService = {
  getAll: async (params = {}) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/instituciones${qs(params)}`);
    return list.map(mapInstitucion);
  },

  getById: async (id) => {
    if (USE_MOCK) return null;
    const i = await fetchApi(`/instituciones/${id}`);
    return mapInstitucionDetalle(i);
  },

  create: async (data) => fetchApi('/instituciones', {
    method: 'POST',
    body: JSON.stringify({
      nombre: data.name, nombreCorto: data.shortName, nit: data.nit, tipo: data.type,
      logoUrl: data.logoUrl, ecolabEntidadId: data.ecolabEntidadId, ecolabTipoEntidad: data.ecolabTipoEntidad,
      paisId: data.countryId || 1045, paisNombre: data.countryName || 'Colombia',
      departamentoId: data.departmentId, departamentoNombre: data.departmentName,
      municipioId: data.municipalityId, municipioNombre: data.municipalityName,
      direccion: data.address, latitud: data.latitude, longitud: data.longitude,
      telefono: data.phone, emailInstitucional: data.email, sitioWeb: data.website,
      // Admin de la alianza (SuperAdmin institucional) -- ver
      // InstitucionesController.Create en el backend. Si adminUsuarioId
      // viene informado, el backend reusa ese usuario de Ecolab; si no,
      // lo crea con los demás campos adminX.
      adminUsuarioId: data.adminUsuarioId || undefined,
      adminEmail: data.adminEmail, adminNombre: data.adminNombre, adminApellido: data.adminApellido,
      adminCelular: data.adminCelular, adminDocumento: data.adminDocumento, adminTipoDocumento: data.adminTipoDocumento,
    }),
  }),

  update: async (id, data) => fetchApi(`/instituciones/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      nombre: data.name, nombreCorto: data.shortName, nit: data.nit, tipo: data.type,
      logoUrl: data.logoUrl, ecolabEntidadId: data.ecolabEntidadId, ecolabTipoEntidad: data.ecolabTipoEntidad,
      paisId: data.countryId || 1045, paisNombre: data.countryName || 'Colombia',
      departamentoId: data.departmentId, departamentoNombre: data.departmentName,
      municipioId: data.municipalityId, municipioNombre: data.municipalityName,
      direccion: data.address, latitud: data.latitude, longitud: data.longitude,
      telefono: data.phone, emailInstitucional: data.email, sitioWeb: data.website,
    }),
  }),

  toggleActiva: async (id) => fetchApi(`/instituciones/${id}/toggle-activa`, { method: 'PATCH' }),

  getUsuarios: async (id) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/instituciones/${id}/usuarios`);
    return list.map((u) => ({
      id: u.usuarioId, name: u.nombre, email: u.email, avatar: gsToHttps(u.fotoUrl),
      linkType: u.tipoVinculo, linkedAt: u.fechaVinculacion,
    }));
  },

  vincularUsuario: async (id, usuarioId, tipoVinculo) =>
    fetchApi(`/instituciones/${id}/usuarios`, { method: 'POST', body: JSON.stringify({ usuarioId, tipoVinculo }) }),

  desvincularUsuario: async (id, usuarioId) =>
    fetchApi(`/instituciones/${id}/usuarios/${usuarioId}`, { method: 'DELETE' }),
};

// ═══════════════════════════════════════════════════════════════════════
//  PEI
// ═══════════════════════════════════════════════════════════════════════
export const peiService = {
  getActivo: async (institucionId) => {
    if (USE_MOCK) return null;
    const p = await fetchApi(`/instituciones/${institucionId}/pei`);
    return mapPei(p);
  },

  getHistorial: async (institucionId) => fetchApi(`/instituciones/${institucionId}/pei/historial`),

  create: async (institucionId, data) => fetchApi(`/instituciones/${institucionId}/pei`, {
    method: 'POST',
    body: JSON.stringify({
      mision: data.mission, vision: data.vision, filosofia: data.philosophy,
      modeloPedagogico: data.pedagogicalModel, enfoqueCurricular: data.curricularApproach,
      calendario: data.calendar || 'A', jornada: data.schedule, modalidad: data.modality || 'Presencial',
      poblacionObjetivo: data.targetPopulation, contextoSocial: data.socialContext,
      manualConvivenciaUrl: data.coexistenceManualUrl, reglamentoUrl: data.regulationsUrl,
      nivelIds: data.levelIds || [],
      valores: (data.values || []).map((v) => ({ nombre: v.name, descripcion: v.description, orden: v.order })),
      areas: (data.areas || []).map((a) => ({
        nombre: a.name, descripcion: a.description, horasSemanales: a.weeklyHours,
        obligatoria: a.mandatory !== false, orden: a.order,
      })),
    }),
  }),
};

const mapPei = (p) => ({
  id: p.id, institutionId: p.institucionId, version: p.version,
  mission: p.mision, vision: p.vision, philosophy: p.filosofia,
  pedagogicalModel: p.modeloPedagogico, curricularApproach: p.enfoqueCurricular,
  calendar: p.calendario, schedule: p.jornada, modality: p.modalidad,
  targetPopulation: p.poblacionObjetivo, socialContext: p.contextoSocial,
  coexistenceManualUrl: gsToHttps(p.manualConvivenciaUrl), regulationsUrl: gsToHttps(p.reglamentoUrl),
  active: p.activo, createdAt: p.fechaCreacion,
  levels: (p.niveles || []).map((n) => ({ levelId: n.nivelId, name: n.nombre })),
  values: (p.valores || []).map((v) => ({ name: v.nombre, description: v.descripcion, order: v.orden })),
  areas: (p.areas || []).map((a) => ({
    name: a.nombre, description: a.descripcion, weeklyHours: a.horasSemanales,
    mandatory: a.obligatoria, order: a.orden,
  })),
});

// ═══════════════════════════════════════════════════════════════════════
//  PERÍODOS ACADÉMICOS
// ═══════════════════════════════════════════════════════════════════════
export const periodoService = {
  getAll: async (institucionId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/instituciones/${institucionId}/periodos`);
    return list.map((p) => ({
      id: p.id, institutionId: p.institucionId, name: p.nombre,
      startDate: p.fechaInicio, endDate: p.fechaFin, active: p.activo, current: p.esActual,
    }));
  },

  create: async (institucionId, data) => fetchApi(`/instituciones/${institucionId}/periodos`, {
    method: 'POST',
    body: JSON.stringify({ nombre: data.name, fechaInicio: data.startDate, fechaFin: data.endDate, esActual: !!data.current }),
  }),

  setActual: async (institucionId, id) =>
    fetchApi(`/instituciones/${institucionId}/periodos/${id}/activar`, { method: 'PATCH' }),
};

// ═══════════════════════════════════════════════════════════════════════
//  MATRÍCULAS INSTITUCIONALES
// ═══════════════════════════════════════════════════════════════════════
const mapMatriculaInst = (m) => ({
  id: m.id, institutionId: m.institucionId, studentId: m.usuarioId,
  studentName: m.estudianteNombre, studentEmail: m.estudianteEmail,
  periodId: m.periodoId, periodName: m.periodoNombre,
  levelId: m.nivelId, levelName: m.nivelNombre, gradeId: m.gradoId, gradeName: m.gradoNombre,
  status: m.estado, enrollmentNumber: m.numeroMatricula, enrolledAt: m.fechaMatricula,
  pendingBalance: m.saldoPendiente, overdueInstallments: m.cuotasVencidas,
});

export const matriculaInstitucionalService = {
  getAll: async (institucionId, params = {}) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/instituciones/${institucionId}/matriculas${qs(params)}`);
    return list.map(mapMatriculaInst);
  },

  getById: async (institucionId, id) => {
    const m = await fetchApi(`/instituciones/${institucionId}/matriculas/${id}`);
    return mapMatriculaInst(m);
  },

  create: async (institucionId, data) => fetchApi(`/instituciones/${institucionId}/matriculas`, {
    method: 'POST',
    body: JSON.stringify({
      usuarioId: data.studentId, periodoId: data.periodId, nivelId: data.levelId, gradoId: data.gradeId,
      numeroMatricula: data.enrollmentNumber, acudienteNombre: data.guardianName,
      acudienteTelefono: data.guardianPhone, acudienteEmail: data.guardianEmail,
      observaciones: data.notes, generarCuotas: data.generateInstallments !== false,
    }),
  }),

  cambiarEstado: async (institucionId, id, estado) =>
    fetchApi(`/instituciones/${institucionId}/matriculas/${id}/estado`, { method: 'PATCH', body: JSON.stringify(estado) }),
};

// ═══════════════════════════════════════════════════════════════════════
//  PENSIONES
// ═══════════════════════════════════════════════════════════════════════
const mapCuota = (c) => ({
  id: c.id, number: c.numeroCuota, description: c.descripcion,
  baseValue: c.valorBase, discount: c.descuento, lateCharge: c.recargoMora, finalValue: c.valorFinal,
  dueDate: c.fechaVencimiento, status: c.estado, totalPaid: c.totalPagado, balance: c.saldo,
});

const mapPago = (p) => ({
  id: p.id, installmentId: p.cuotaId, installmentNumber: p.numeroCuota,
  amount: p.monto, method: p.metodo, reference: p.referenciaPago,
  paidAt: p.fechaPago, registeredBy: p.registradoPorNombre, voided: p.anulado,
});

export const pensionService = {
  getTarifas: async (institucionId, periodoId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/instituciones/${institucionId}/pensiones/tarifas${qs({ periodoId })}`);
    return list.map((t) => ({
      id: t.id, periodId: t.periodoId, levelId: t.nivelId, levelName: t.nivelNombre,
      gradeId: t.gradoId, gradeName: t.gradoNombre, totalValue: t.valorTotal,
      installments: t.numeroCuotas, enrollmentValue: t.valorMatricula,
      lateChargePercent: t.porcentajeMora, gracePeriodDays: t.diasGracia, active: t.activa,
    }));
  },

  createTarifa: async (institucionId, data) => fetchApi(`/instituciones/${institucionId}/pensiones/tarifas`, {
    method: 'POST',
    body: JSON.stringify({
      periodoId: data.periodId, nivelId: data.levelId, gradoId: data.gradeId,
      valorTotal: data.totalValue, numeroCuotas: data.installments || 10,
      valorMatricula: data.enrollmentValue || 0, descuentoHermanos: data.siblingDiscount || 0,
      porcentajeMora: data.lateChargePercent ?? 2, diasGracia: data.gracePeriodDays ?? 5,
    }),
  }),

  getCuotas: async (institucionId, matriculaId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/instituciones/${institucionId}/pensiones/matriculas/${matriculaId}/cuotas`);
    return list.map(mapCuota);
  },

  getPagos: async (institucionId, matriculaId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/instituciones/${institucionId}/pensiones/matriculas/${matriculaId}/pagos`);
    return list.map(mapPago);
  },

  registrarPago: async (institucionId, matriculaId, data) =>
    fetchApi(`/instituciones/${institucionId}/pensiones/matriculas/${matriculaId}/pagos`, {
      method: 'POST',
      body: JSON.stringify({
        cuotaId: data.installmentId, monto: data.amount, metodo: data.method,
        referenciaPago: data.reference, comprobanteUrl: data.receiptUrl,
        fechaPago: data.paidAt, observaciones: data.notes,
      }),
    }),

  exonerarCuota: async (institucionId, matriculaId, cuotaId, motivo) =>
    fetchApi(`/instituciones/${institucionId}/pensiones/matriculas/${matriculaId}/exonerar`, {
      method: 'POST', body: JSON.stringify({ cuotaId, motivoExoneracion: motivo }),
    }),

  anularPago: async (institucionId, pagoId, motivo) =>
    fetchApi(`/instituciones/${institucionId}/pensiones/pagos/${pagoId}`, {
      method: 'DELETE', body: JSON.stringify(motivo),
    }),

  getResumen: async (institucionId, periodoId) => {
    if (USE_MOCK) return null;
    const r = await fetchApi(`/instituciones/${institucionId}/pensiones/resumen${qs({ periodoId })}`);
    return {
      totalEnrollments: r.totalMatriculas, totalPortfolio: r.carteraTotal,
      collected: r.recaudado, pending: r.porCobrar, overdue: r.enMora, exempted: r.exonerado,
    };
  },

  subirComprobante: async (pagoId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/pagos/${pagoId}/comprobante`, { method: 'POST', body: fd });
  },

  // ── Pago automático vía Wompi (misma cuenta comercial de Fenagro,
  // ver PagoPensionController en el backend) ────────────────────────
  iniciarPagoWompi: async (cuotaId) =>
    fetchApi(`/pensiones/pago/iniciar/${cuotaId}`, { method: 'POST' }),
};

// ═══════════════════════════════════════════════════════════════════════
//  CATÁLOGOS
// ═══════════════════════════════════════════════════════════════════════
export const catalogoService = {
  getNivelesEducativos: async () => {
    if (USE_MOCK) return [];
    const list = await fetchApi('/catalogos/niveles-educativos');
    return list.map((n) => ({ id: n.id, type: n.tipo, name: n.nombre, shortName: n.nombreCorto }));
  },

  getGrados: async (nivelId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/catalogos/niveles-educativos/${nivelId}/grados`);
    return list.map((g) => ({ id: g.id, levelId: g.nivelId, name: g.nombre, shortName: g.nombreCorto }));
  },

  getCategorias: async () => {
    if (USE_MOCK) return [];
    const list = await fetchApi('/catalogos/categorias');
    return list.map((c) => ({ id: c.id, name: c.nombre, icon: c.icono, color: c.color }));
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  GEOGRAFÍA (proxy a Ecolab)
// ═══════════════════════════════════════════════════════════════════════
// Geografía: llama DIRECTO a Ecolab (fuente única del catálogo de
// países/departamentos/municipios) — mismos endpoints públicos
// (AllowAnonymous) que usa fenagro_front. No pasa por el backend de
// EducaT, evitando una capa de proxy innecesaria.
const _ecolabGet = async (path) => {
  const url = `${ECOLAB_API_URL}${path}`;
  console.log('[geografiaService] Fetching:', url);
  try {
    const res = await fetch(url);
    console.log('[geografiaService] Response status:', res.status, 'ok:', res.ok);
    if (!res.ok) {
      const bodyText = await res.text().catch(() => '(sin body)');
      console.error('[geografiaService] Error body:', bodyText);
      throw new Error(`Ecolab GET ${path} → ${res.status}`);
    }
    const data = await res.json();
    console.log('[geografiaService] Data:', data);
    return data;
  } catch (err) {
    console.error('[geografiaService] Fetch falló completamente:', err);
    return [];
  }
};

export const geografiaService = {
  getPaises: async () => (USE_MOCK ? [] : _ecolabGet('/api/ecolab/geo/paises')),
  getDepartamentos: async (paisId = 1045) => (USE_MOCK ? [] : _ecolabGet(`/api/ecolab/geo/departamentos?paisId=${paisId}`)),
  getMunicipios: async (departamentoId) => (USE_MOCK ? [] : _ecolabGet(`/api/ecolab/geo/municipios?departamentoId=${departamentoId}`)),

  // Municipios CON lat/lng — para centrar el mapa automáticamente al
  // elegir un municipio, sin depender de Geocoding.
  getMunicipiosConCoordenadas: async (departamentoId) =>
    (USE_MOCK ? [] : _ecolabGet(`/api/ecolab/geo/municipios/con-coordenadas?departamentoId=${departamentoId}`)),

  // Geolocalización inversa real de Ecolab: dado (lat,lng) devuelve el
  // municipio más cercano (fórmula haversine sobre su catálogo con
  // coordenadas) — usado por el botón "Usar mi ubicación".
  getMunicipioMasCercano: async (lat, lng, paisId = 1045) => {
    if (USE_MOCK) return null;
    try {
      const res = await fetch(`${ECOLAB_API_URL}/api/ecolab/geo/municipios/mas-cercano?lat=${lat}&lng=${lng}&paisId=${paisId}`);
      if (!res.ok) return null;
      return res.json();
    } catch {
      return null;
    }
  },


  // Geocodificación — llama DIRECTO a Ecolab (no pasa por el backend de
  // EducaT), mismo endpoint público que usa fenagro_front en su
  // UbicacionMapaPicker: /api/ecolab/geocoding/codigo-postal.
  geocodificarDireccion: async (direccion, ciudad = '', depto = '', pais = 'Colombia') => {
    const params = new URLSearchParams({
      direccion: direccion || '', ciudad: ciudad || '', depto: depto || '', pais: pais || 'Colombia',
    });
    try {
      const res = await fetch(`${ECOLAB_API_URL}/api/ecolab/geocoding/codigo-postal?${params}`);
      if (!res.ok) {
        return { codigoPostal: null, lat: null, lng: null, status: 'BACKEND_ERROR', httpStatus: res.status };
      }
      const data = await res.json();
      return {
        codigoPostal: data.codigoPostal ?? null,
        lat: data.lat ?? null,
        lng: data.lng ?? null,
        status: data.status || 'UNKNOWN',
        httpStatus: res.status,
      };
    } catch {
      return { codigoPostal: null, lat: null, lng: null, status: 'FETCH_ERROR', httpStatus: null };
    }
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  CURSOS
// ═══════════════════════════════════════════════════════════════════════
const mapCursoList = (c) => ({
  id: c.id, slug: c.slug, title: c.titulo, subtitle: c.subtitulo, image: gsToHttps(c.imagenUrl),
  level: c.nivel, category: c.categoriaNombre, categoryId: c.categoriaId ?? null, institutionId: c.institucionId ?? null, instructorId: c.instructorId, instructor: c.instructorNombre,
  institution: c.institucionNombre, isGlobal: c.esGlobal, active: c.activo, published: c.publicado,
  totalEnrollments: c.totalMatriculas, educationLevel: c.nivelEducativoNombre, grade: c.gradoNombre,
});

const mapCursoDetalle = (c) => ({
  ...mapCursoList(c),
  description: c.descripcion, credits: c.creditos, durationWeeks: c.duracionSemanas,
  gradingWeights: {
    quizzes: c.pesoQuizzes, activities: c.pesoActividades,
    midterm: c.pesoExamenParcial, final: c.pesoExamenFinal, project: c.pesoProyecto,
  },
  periodId: c.periodoId,
  units: (c.unidades || []).map(mapUnidad),
});

const mapUnidad = (u) => ({
  id: u.id, title: u.titulo, description: u.descripcion, order: u.orden, active: u.activa,
  contents: (u.contenidos || []).map(mapContenido),
});

const mapContenido = (ct) => ({
  id: ct.id, type: ct.tipo, title: ct.titulo, description: ct.descripcion, order: ct.orden,
  body: ct.textoCuerpo,
  resourceUrl: ct.urlRecurso, durationMinutes: ct.duracionMinutos, minScore: ct.puntajeMinimo,
  maxAttempts: ct.maxIntentos, maxPoints: ct.puntosMaximos, requiresPrevious: ct.requiereAnterior,
  active: ct.activo,
});

export const cursoService = {
  getAll: async (params = {}) => {
    if (USE_MOCK) return { data: [], total: 0, page: 1, totalPages: 1 };
    const paged = await fetchApi(`/cursos${qs({
      institucionId: params.institutionId, esGlobal: params.isGlobal, categoriaId: params.categoryId,
      nivel: params.level, pagina: params.page || 1, tamano: params.limit || 20,
    })}`);
    return { data: (paged.items || []).map(mapCursoList), total: paged.total, page: paged.pagina, totalPages: Math.ceil(paged.total / paged.tamano) };
  },

  getById: async (id) => {
    if (USE_MOCK) return null;
    const c = await fetchApi(`/cursos/${id}`);
    return mapCursoDetalle(c);
  },

  getBySlug: async (slug) => {
    if (USE_MOCK) return null;
    const c = await fetchApi(`/cursos/slug/${encodeURIComponent(slug)}`);
    return mapCursoDetalle(c);
  },

  create: async (data) => fetchApi('/cursos', {
    method: 'POST',
    body: JSON.stringify({
      slug: data.slug, titulo: data.title, subtitulo: data.subtitle, descripcion: data.description,
      imagenUrl: data.imageGsUri || undefined,
      categoriaId: data.categoryId ?? (Number.isInteger(Number(data.category)) && data.category !== '' ? Number(data.category) : undefined),
      nivel: data.level || undefined,
      instructorId: data.instructorId, institucionId: data.institutionId,
      esGlobal: !!data.isGlobal, visibleRed: !!data.visibleToNetwork,
      nivelEducativoId: data.educationLevelId, gradoId: data.gradeId, periodoId: data.periodId,
      creditos: data.credits || 3, duracionSemanas: data.durationWeeks || 16,
      pesoQuizzes: data.gradingWeights?.quizzes ?? 20, pesoActividades: data.gradingWeights?.activities ?? 30,
      pesoExamenParcial: data.gradingWeights?.midterm ?? 20, pesoExamenFinal: data.gradingWeights?.final ?? 20,
      pesoProyecto: data.gradingWeights?.project ?? 10,
    }),
  }),

  // PUT con semantica parcial en el backend: los campos undefined no viajan
  // en el JSON y el backend no los toca. Por eso aqui solo se mandan los
  // campos que edita el modal -- nunca slug/instructor/ambito/pesos en
  // blanco, que antes dejaban Slug="" e InstructorId=Guid.Empty (500).
  // La imagen la sube cursoService.subirImagen; solo se reenvia el gs://.
  update: async (id, data) => fetchApi(`/cursos/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      titulo: data.title, subtitulo: data.subtitle, descripcion: data.description,
      categoriaId: Number.isInteger(Number(data.category)) && data.category !== '' ? Number(data.category) : undefined,
      nivel: data.level || undefined,
      creditos: Number.isFinite(data.credits) ? data.credits : undefined,
      imagenUrl: data.imageGsUri || undefined,
      // Para reasignar instructor (tab Instructores); el modal no lo manda.
      instructorId: data.instructorId || undefined,
    }),
  }),

  publicar: async (id) => fetchApi(`/cursos/${id}/publicar`, { method: 'PATCH' }),

  // Sube la imagen hero del curso. El backend la guarda en GCS en
  // cursos/{cursoId}/hero y actualiza cursos.imagen_url. Devuelve
  // { gsUri, signedUrl } — signedUrl es una URL firmada temporal
  // (24h) lista para mostrar en <img src="...">; gsUri es la que
  // queda almacenada en BD.
  subirImagen: async (cursoId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/cursos/${cursoId}/imagen`, { method: 'POST', body: fd });
  },

  getUnidades: async (cursoId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/cursos/${cursoId}/unidades`);
    return list.map(mapUnidad);
  },

  createUnidad: async (cursoId, data) => fetchApi(`/cursos/${cursoId}/unidades`, {
    method: 'POST', body: JSON.stringify({ titulo: data.title, descripcion: data.description, orden: data.order || 1 }),
  }),

  updateUnidad: async (cursoId, unidadId, data) => fetchApi(`/cursos/${cursoId}/unidades/${unidadId}`, {
    method: 'PUT', body: JSON.stringify({ titulo: data.title, descripcion: data.description, orden: data.order || 1 }),
  }),

  getContenidos: async (cursoId, unidadId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/cursos/${cursoId}/unidades/${unidadId}/contenidos`);
    return list.map(mapContenido);
  },

  createContenido: async (cursoId, unidadId, data) => fetchApi(`/cursos/${cursoId}/unidades/${unidadId}/contenidos`, {
    method: 'POST',
    body: JSON.stringify({
      tipo: data.type, titulo: data.title, descripcion: data.description, orden: data.order || 1,
      urlRecurso: data.resourceUrl, textoCuerpo: data.bodyText, duracionMinutos: data.durationMinutes,
      puntajeMinimo: data.minScore, maxIntentos: data.maxAttempts, puntosMaximos: data.maxPoints || 0,
      requiereAnterior: data.requiresPrevious !== false,
    }),
  }),

  updateContenido: async (cursoId, unidadId, contenidoId, data) => fetchApi(
    `/cursos/${cursoId}/unidades/${unidadId}/contenidos/${contenidoId}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        tipo: data.type, titulo: data.title, descripcion: data.description, orden: data.order || 1,
        urlRecurso: data.resourceUrl, textoCuerpo: data.bodyText, duracionMinutos: data.durationMinutes,
        puntajeMinimo: data.minScore, maxIntentos: data.maxAttempts, puntosMaximos: data.maxPoints || 0,
        requiereAnterior: data.requiresPrevious !== false,
      }),
    }
  ),

  deleteContenido: async (cursoId, unidadId, contenidoId) => fetchApi(
    `/cursos/${cursoId}/unidades/${unidadId}/contenidos/${contenidoId}`,
    { method: 'DELETE' }
  ),

  deleteUnidad: async (cursoId, unidadId) => fetchApi(
    `/cursos/${cursoId}/unidades/${unidadId}`,
    { method: 'DELETE' }
  ),

  subirImagen: async (cursoId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/cursos/${cursoId}/imagen`, { method: 'POST', body: fd });
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  APRENDIZAJE (matrícula de curso, progreso)
// ═══════════════════════════════════════════════════════════════════════
export const aprendizajeService = {
  matricular: async (cursoId, matriculaInstId = null, terminos = null) =>
    fetchApi('/aprendizaje/matricular', {
      method: 'POST',
      body: JSON.stringify({
        cursoId, matriculaInstId,
        terminosAceptados: !!terminos?.aceptados,
        terminosVersion: terminos?.version || null,
      }),
    }),

  registrarAcceso: async (matriculaId, contenidoId) =>
    fetchApi('/aprendizaje/acceso', {
      method: 'POST',
      body: JSON.stringify({ matriculaId, contenidoId }),
    }).catch(() => null),   // no bloquea UI si falla

  getProgreso: async (matriculaId) => {
    if (USE_MOCK) return null;
    const p = await fetchApi(`/aprendizaje/matriculas/${matriculaId}/progreso`);
    return {
      enrollmentId: p.matriculaId, courseId: p.cursoId, progress: p.progreso,
      finalGrade: p.calificacion, status: p.estado,
      contents: (p.contenidos || []).map((c) => ({
        progressId: c.progresoId,
        contentId: c.contenidoId, type: c.tipo, title: c.titulo, status: c.estado,
        videoPercent: c.porcentajeVideo, scoreObtained: c.puntajeObtenido,
        attemptsMade: c.intentosRealizados, completed: c.completado, completedAt: c.fechaCompletado,
      })),
    };
  },

  marcarCompletado: async (matriculaId, contenidoId) =>
    fetchApi(`/aprendizaje/matriculas/${matriculaId}/completar/${contenidoId}`, { method: 'POST' }),

  actualizarVideoProgreso: async (matriculaId, contenidoId, porcentaje) =>
    fetchApi('/aprendizaje/video-progreso', {
      method: 'POST', body: JSON.stringify({ matriculaId, contenidoId, porcentaje }),
    }),

  getEstudiantesCurso: async (cursoId, params = {}) => {
    if (USE_MOCK) return { data: [], total: 0 };
    const paged = await fetchApi(`/aprendizaje/cursos/${cursoId}/estudiantes${qs(params)}`);
    return {
      data: (paged.items || []).map((m) => ({
        enrollmentId: m.id, studentId: m.usuarioId, studentName: m.usuarioNombre,
        status: m.estado, progress: m.porcentajeProgreso, finalGrade: m.calificacionFinal,
        enrolledAt: m.fechaMatricula, completedAt: m.fechaCompletado,
      })),
      total: paged.total,
    };
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  EVALUACIONES (Quiz / Examen)
// ═══════════════════════════════════════════════════════════════════════
export const evaluacionService = {
  getPreguntas: async (contenidoId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/evaluaciones/contenidos/${contenidoId}/preguntas`);
    return list.map(mapPregunta);
  },

  getQuizParaEstudiante: async (contenidoId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/evaluaciones/contenidos/${contenidoId}/quiz`);
    return list.map(mapPregunta);
  },

  createPregunta: async (contenidoId, data) => fetchApi(`/evaluaciones/contenidos/${contenidoId}/preguntas`, {
    method: 'POST',
    body: JSON.stringify({
      enunciado: data.statement, explicacion: data.explanation, orden: data.order || 1,
      puntos: data.points || 1,
      opciones: (data.options || []).map((o) => ({ texto: o.text, esCorrecta: !!o.isCorrect, orden: o.order })),
    }),
  }),

  updatePregunta: async (preguntaId, data) => fetchApi(`/evaluaciones/preguntas/${preguntaId}`, {
    method: 'PUT',
    body: JSON.stringify({
      enunciado: data.statement, explicacion: data.explanation, orden: data.order || 1,
      puntos: data.points || 1,
      // Si la opcion viene con id, el backend la actualiza; sin id, la crea.
      // Las opciones que no esten en este array se eliminan.
      opciones: (data.options || []).map((o) => ({
        id: o.id || null,
        texto: o.text,
        esCorrecta: !!o.isCorrect,
        orden: o.order,
      })),
    }),
  }),

  deletePregunta: async (preguntaId) =>
    fetchApi(`/evaluaciones/preguntas/${preguntaId}`, { method: 'DELETE' }),

  iniciarIntento: async (matriculaId, contenidoId) =>
    fetchApi('/evaluaciones/intentos/iniciar', { method: 'POST', body: JSON.stringify({ matriculaId, contenidoId }) }),

  responderIntento: async (intentoId, respuestas) =>
    fetchApi('/evaluaciones/intentos/responder', {
      method: 'POST',
      body: JSON.stringify({
        intentoId,
        respuestas: respuestas.map((r) => ({ preguntaId: r.questionId, opcionId: r.optionId })),
      }),
    }).then(mapResultadoIntento),
};

const mapPregunta = (p) => ({
  id: p.id, statement: p.enunciado, order: p.orden, points: p.puntos, explanation: p.explicacion,
  options: (p.opciones || []).map((o) => ({ id: o.id, text: o.texto, order: o.orden, isCorrect: o.esCorrecta })),
});

const mapResultadoIntento = (r) => ({
  attemptId: r.intentoId, attemptNumber: r.numeroIntento, scoreObtained: r.puntajeObtenido,
  maxScore: r.puntajeMaximo, percent: r.porcentaje, passed: r.aprobado, timeSeconds: r.tiempoSegundos,
  detail: (r.detalle || []).map((d) => ({
    questionId: d.preguntaId, statement: d.enunciado, chosenOptionId: d.opcionElegida,
    isCorrect: d.esCorrecta, explanation: d.explicacion, correctOptionId: d.opcionCorrectaId,
  })),
});

// ═══════════════════════════════════════════════════════════════════════
//  ENTREGAS (Actividades)
// ═══════════════════════════════════════════════════════════════════════
const mapEntrega = (e) => ({
  id: e.id, progressId: e.progresoId, contentId: e.contenidoId, contentTitle: e.contenidoTitulo,
  courseId: e.cursoId, courseTitle: e.cursoTitulo,
  studentId: e.estudianteId, studentName: e.estudianteNombre, status: e.estado,
  studentComment: e.comentarioAlumno, submittedAt: e.fechaEnvio,
  score: e.puntaje, maxScore: e.puntajeMaximo, instructorComment: e.comentarioInstructor,
  files: (e.archivos || []).map((a) => ({
    id: a.id, fileName: a.nombreArchivo, url: gsToHttps(a.gsUri), mimeType: a.tipoMime, size: a.tamanoBytes,
  })),
});

export const entregaService = {
  getPendientesInstructor: async (cursoId = null) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/entregas/pendientes-instructor${qs({ cursoId })}`);
    return list.map(mapEntrega);
  },

  getById: async (id) => {
    if (USE_MOCK) return null;
    const e = await fetchApi(`/entregas/${id}`);
    return mapEntrega(e);
  },

  create: async (progresoId, comentarioAlumno, archivos = []) =>
    fetchApi('/entregas', {
      method: 'POST',
      body: JSON.stringify({
        progresoId, comentarioAlumno,
        archivos: archivos.map((a) => ({ nombreArchivo: a.fileName, gsUri: a.gsUri, tipoMime: a.mimeType, tamanoBytes: a.size })),
      }),
    }),

  subirArchivo: async (entregaId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/entregas/${entregaId}/archivo`, { method: 'POST', body: fd });
  },

  eliminarArchivo: async (archivoId) => fetchApi(`/storage/entregas/archivos/${archivoId}`, { method: 'DELETE' }),

  calificar: async (entregaId, score, comment, rubrica = null) =>
    fetchApi(`/entregas/${entregaId}/calificar`, {
      method: 'POST',
      body: JSON.stringify({
        entregaId, puntaje: score, comentarioInstructor: comment,
        rubrica: rubrica ? rubrica.map((r) => ({ criterioId: r.criterionId, puntos: r.points, observacion: r.note })) : null,
      }),
    }),
};

// ═══════════════════════════════════════════════════════════════════════
//  RÚBRICAS
// ═══════════════════════════════════════════════════════════════════════
const mapRubrica = (r) => ({
  id: r.id, name: r.nombre,
  criteria: (r.criterios || []).map((c) => ({
    id: c.id, name: c.nombre, description: c.descripcion,
    weightPercent: c.pesoPorcentaje, maxPoints: c.puntosMaximos, order: c.orden,
  })),
});

export const rubricaService = {
  getByContenido: async (contenidoId) => {
    if (USE_MOCK) return null;
    try { return mapRubrica(await fetchApi(`/rubricas/contenidos/${contenidoId}`)); }
    catch { return null; }
  },

  create: async (contenidoId, data) => fetchApi(`/rubricas/contenidos/${contenidoId}`, {
    method: 'POST',
    body: JSON.stringify({
      nombre: data.name, descripcion: data.description,
      criterios: (data.criteria || []).map((c) => ({
        nombre: c.name, descripcion: c.description, pesoPorcentaje: c.weightPercent,
        puntosMaximos: c.maxPoints, orden: c.order || 1,
      })),
    }),
  }),

  update: async (id, data) => fetchApi(`/rubricas/${id}`, {
    method: 'PUT', body: JSON.stringify({ nombre: data.name, descripcion: data.description, activa: data.active !== false }),
  }),

  delete: async (id) => fetchApi(`/rubricas/${id}`, { method: 'DELETE' }),

  addCriterio: async (rubricaId, data) => fetchApi(`/rubricas/${rubricaId}/criterios`, {
    method: 'POST',
    body: JSON.stringify({ nombre: data.name, descripcion: data.description, pesoPorcentaje: data.weightPercent, puntosMaximos: data.maxPoints, orden: data.order || 1 }),
  }),

  updateCriterio: async (criterioId, data) => fetchApi(`/rubricas/criterios/${criterioId}`, {
    method: 'PUT',
    body: JSON.stringify({ nombre: data.name, descripcion: data.description, pesoPorcentaje: data.weightPercent, puntosMaximos: data.maxPoints, orden: data.order || 1 }),
  }),

  deleteCriterio: async (criterioId) => fetchApi(`/rubricas/criterios/${criterioId}`, { method: 'DELETE' }),

  validar: async (rubricaId) => fetchApi(`/rubricas/${rubricaId}/validar`),
};

// ═══════════════════════════════════════════════════════════════════════
//  SESIONES ONLINE (Google Meet)
// ═══════════════════════════════════════════════════════════════════════
const mapSesionOnline = (s) => ({
  id: s.id, courseId: s.cursoId, courseTitle: s.cursoTitulo, title: s.titulo, type: s.tipo,
  status: s.estado, meetLink: s.meetLink, startAt: s.fechaInicio, durationMinutes: s.duracionMinutos,
  instructorName: s.instructorNombre, externalSpeaker: s.ponenteExterno,
  enrolled: s.inscritos, isEnrolled: s.estaInscrito, recordingUrl: gsToHttps(s.recordingUrl),
});

export const sesionOnlineService = {
  getAll: async (params = {}) => {
    if (USE_MOCK) return { data: [], total: 0 };
    const paged = await fetchApi(`/sesiones/online${qs({
      cursoId: params.courseId, instId: params.institutionId, estado: params.status,
      pagina: params.page || 1, tamano: params.limit || 20,
    })}`);
    return { data: (paged.items || []).map(mapSesionOnline), total: paged.total };
  },

  getById: async (id) => {
    if (USE_MOCK) return null;
    const s = await fetchApi(`/sesiones/online/${id}`);
    return {
      id: s.id, courseId: s.cursoId, courseTitle: s.cursoTitulo, title: s.titulo,
      description: s.descripcion, type: s.tipo, status: s.estado, meetLink: s.meetLink,
      startAt: s.fechaInicio, durationMinutes: s.duracionMinutos, maxParticipants: s.maxParticipantes,
      instructorName: s.instructorNombre, externalSpeaker: s.ponenteExterno,
      recordingUrl: gsToHttps(s.recordingUrl), notes: s.notasSesion,
      enrolled: s.inscritos, isEnrolled: s.estaInscrito,
      materials: (s.materiales || []).map((m) => ({ id: m.id, name: m.nombre, url: gsToHttps(m.gsUri) || m.urlExterna, mimeType: m.tipoMime })),
    };
  },

  create: async (data) => fetchApi('/sesiones/online', {
    method: 'POST',
    body: JSON.stringify({
      cursoId: data.courseId, institucionId: data.institutionId, contenidoId: data.contentId,
      titulo: data.title, descripcion: data.description, tipo: data.type || 'ClaseEnVivo',
      instructorId: data.instructorId, ponenteExterno: data.externalSpeaker,
      fechaInicio: data.startAt, duracionMinutos: data.durationMinutes || 60, maxParticipantes: data.maxParticipants,
    }),
  }),

  setMeetLink: async (id, meetLink, meetEventId, meetCalendarId) =>
    fetchApi(`/sesiones/online/${id}/meet-link`, {
      method: 'PATCH', body: JSON.stringify({ meetLink, meetEventId, meetCalendarId }),
    }),

  cambiarEstado: async (id, estado) => fetchApi(`/sesiones/online/${id}/estado`, { method: 'PATCH', body: JSON.stringify(estado) }),

  setRecording: async (id, recordingUrl, notas) =>
    fetchApi(`/sesiones/online/${id}/recording`, { method: 'POST', body: JSON.stringify({ recordingUrl, notasSesion: notas }) }),

  inscribir: async (id) => fetchApi(`/sesiones/online/${id}/inscribir`, { method: 'POST' }),
  desinscribir: async (id) => fetchApi(`/sesiones/online/${id}/inscribir`, { method: 'DELETE' }),

  registrarAsistencia: async (id) => fetchApi(`/sesiones/online/${id}/asistencia`, { method: 'POST' }),

  getAsistencia: async (id) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/sesiones/online/${id}/asistencia`);
    return list.map((a) => ({ userId: a.usuarioId, name: a.nombre, email: a.email, joinedAt: a.timestampUnion, confirmed: a.asistioConfirmado }));
  },

  subirMaterial: async (sesionId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/sesiones-online/${sesionId}/material`, { method: 'POST', body: fd });
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  SESIONES GRABADAS
// ═══════════════════════════════════════════════════════════════════════
export const sesionGrabadaService = {
  getAll: async (params = {}) => {
    if (USE_MOCK) return { data: [], total: 0 };
    const paged = await fetchApi(`/sesiones/grabadas${qs({
      cursoId: params.courseId, pagina: params.page || 1, tamano: params.limit || 20,
    })}`);
    return {
      data: (paged.items || []).map((s) => ({
        id: s.id, courseId: s.cursoId, courseTitle: s.cursoTitulo, title: s.titulo,
        description: s.descripcion, instructorName: s.instructorNombre, videoUrl: s.urlVideo,
        sourceType: s.tipoFuente, durationSeconds: s.duracionSegundos, thumbnail: gsToHttps(s.miniaturaUrl),
        active: s.activa, watchedPercent: s.porcentajeVisto,
      })),
      total: paged.total,
    };
  },

  create: async (data) => fetchApi('/sesiones/grabadas', {
    method: 'POST',
    body: JSON.stringify({
      cursoId: data.courseId, institucionId: data.institutionId, contenidoId: data.contentId,
      titulo: data.title, descripcion: data.description, instructorId: data.instructorId,
      urlVideo: data.videoUrl, tipoFuente: data.sourceType || 'YouTube',
      duracionSegundos: data.durationSeconds, miniaturaUrl: data.thumbnail, fechaDisponible: data.availableAt,
    }),
  }),

  actualizarProgreso: async (sesionId, porcentajeVisto, segundosVistos) =>
    fetchApi('/sesiones/grabadas/progreso', {
      method: 'POST', body: JSON.stringify({ sesionId, porcentajeVisto, segundosVistos }),
    }),

  subirMaterial: async (sesionId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/sesiones-grabadas/${sesionId}/material`, { method: 'POST', body: fd });
  },

  subirMiniatura: async (sesionId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/sesiones-grabadas/${sesionId}/miniatura`, { method: 'POST', body: fd });
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  CHAT
// ═══════════════════════════════════════════════════════════════════════
export const chatService = {
  getConversaciones: async () => {
    if (USE_MOCK) return [];
    const list = await fetchApi('/chat/conversaciones');
    return list.map((c) => ({
      id: c.id, courseId: c.cursoId, courseTitle: c.cursoTitulo,
      otherUserId: c.otroUsuarioId, otherUserName: c.otroUsuarioNombre, otherUserAvatar: gsToHttps(c.otroUsuarioFotoUrl),
      lastMessage: c.ultimoMensaje, lastMessageAt: c.fechaUltimo, unread: c.noLeidos,
    }));
  },

  getMensajes: async (conversacionId, page = 1, limit = 50) => {
    if (USE_MOCK) return { data: [], total: 0 };
    const paged = await fetchApi(`/chat/conversaciones/${conversacionId}/mensajes${qs({ pagina: page, tamano: limit })}`);
    return {
      data: (paged.items || []).map((m) => ({
        id: m.id, senderId: m.remitenteId, senderName: m.remitenteNombre,
        body: m.cuerpo, read: m.leido, sentAt: m.fechaEnvio, isMine: m.esMio,
      })),
      total: paged.total,
    };
  },

  enviarMensaje: async (destinatarioId, cuerpo, cursoId = null) =>
    fetchApi('/chat/mensajes', { method: 'POST', body: JSON.stringify({ destinatarioId, cursoId, cuerpo }) }),
};

// ═══════════════════════════════════════════════════════════════════════
//  FOROS
// ═══════════════════════════════════════════════════════════════════════
export const foroService = {
  getTemas: async (cursoId, page = 1, limit = 20) => {
    if (USE_MOCK) return { data: [], total: 0 };
    const paged = await fetchApi(`/foros/cursos/${cursoId}${qs({ pagina: page, tamano: limit })}`);
    return {
      data: (paged.items || []).map(mapTema),
      total: paged.total,
    };
  },

  createTema: async (cursoId, unidadId, titulo, cuerpo) =>
    fetchApi('/foros/temas', { method: 'POST', body: JSON.stringify({ cursoId, unidadId, titulo, cuerpo }) }),

  getRespuestas: async (temaId) => {
    if (USE_MOCK) return [];
    const list = await fetchApi(`/foros/temas/${temaId}/respuestas`);
    return list.map((r) => ({
      id: r.id, authorId: r.autorId, authorName: r.autorNombre, authorAvatar: gsToHttps(r.autorFotoUrl),
      body: r.cuerpo, isCorrect: r.esCorrecta, votes: r.votosUtiles, myVote: r.yaVote, createdAt: r.fechaCreacion,
    }));
  },

  responder: async (temaId, cuerpo) =>
    fetchApi(`/foros/temas/${temaId}/respuestas`, { method: 'POST', body: JSON.stringify({ cuerpo }) }),

  votar: async (respuestaId) => fetchApi(`/foros/respuestas/${respuestaId}/votar`, { method: 'POST' }),

  marcarCorrecta: async (respuestaId) => fetchApi(`/foros/respuestas/${respuestaId}/marcar-correcta`, { method: 'PATCH' }),

  toggleFijar: async (temaId) => fetchApi(`/foros/temas/${temaId}/fijar`, { method: 'PATCH' }),
  toggleCerrar: async (temaId) => fetchApi(`/foros/temas/${temaId}/cerrar`, { method: 'PATCH' }),
};

const mapTema = (t) => ({
  id: t.id, courseId: t.cursoId, unitId: t.unidadId, authorId: t.autorId,
  authorName: t.autorNombre, authorAvatar: gsToHttps(t.autorFotoUrl),
  title: t.titulo, body: t.cuerpo, pinned: t.fijado, closed: t.cerrado,
  totalReplies: t.totalRespuestas, createdAt: t.fechaCreacion,
});

// ═══════════════════════════════════════════════════════════════════════
//  ADMIN
// ═══════════════════════════════════════════════════════════════════════
export const adminService = {
  getUsuarios: async (params = {}) => {
    if (USE_MOCK) return { data: [], total: 0 };
    const paged = await fetchApi(`/admin/usuarios${qs({ rol: params.role, q: params.q, pagina: params.page || 1, tamano: params.limit || 30 })}`);
    return {
      data: (paged.items || []).map((u) => ({
        id: u.id, email: u.email, name: [u.nombre, u.apellido].filter(Boolean).join(' '),
        avatar: gsToHttps(u.fotoUrl), municipality: u.municipioNombre, department: u.departamentoNombre,
        role: mapRolBackendToFrontend(u.rol), roleBackend: u.rol, lastAccess: u.ultimoAcceso,
      })),
      total: paged.total,
    };
  },

  cambiarRol: async (usuarioId, rol) =>
    fetchApi('/admin/usuarios/rol', { method: 'PUT', body: JSON.stringify({ usuarioId, rol }) }),

  getStats: async () => {
    if (USE_MOCK) return null;
    const s = await fetchApi('/admin/stats');
    return {
      totalUsers: s.totalUsuarios, totalInstitutions: s.totalInstituciones,
      totalCourses: s.totalCursos, totalEnrollments: s.totalMatriculas,
      globalCourses: s.cursosGlobales, onlineSessions: s.sesionesOnline, recordedSessions: s.sesionesGrabadas,
    };
  },

  getAuditLog: async (page = 1, limit = 50) => {
    if (USE_MOCK) return { data: [], total: 0 };
    const r = await fetchApi(`/admin/audit-log${qs({ pagina: page, tamano: limit })}`);
    return { data: r.items, total: r.total };
  },
};

// ═══════════════════════════════════════════════════════════════════════
//  STORAGE (genérico)
// ═══════════════════════════════════════════════════════════════════════
export const storageService = {
  subirLogoInstitucion: async (institucionId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/instituciones/${institucionId}/logo`, { method: 'POST', body: fd });
  },

  subirAvatarPropio: async (file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi('/storage/me/avatar', { method: 'POST', body: fd });
  },

  getUrlFirmada: async (gsUri, validHours = 1) => {
    if (USE_MOCK || !gsUri) return null;
    const data = await fetchApi(`/storage/signed-url${qs({ gsUri, validHours })}`);
    return data?.signedUrl;
  },

  eliminar: async (gsUri) => fetchApi(`/storage${qs({ gsUri })}`, { method: 'DELETE' }),
};

// ═══════════════════════════════════════════════════════════════════════
//  Admin — monitoreo de accesos y auditoria de contenido
// ═══════════════════════════════════════════════════════════════════════

export const adminAccesosService = {
  sospechosos: async ({ dias = 7, umbral = 30, ventanaMinutos = 10 } = {}) =>
    fetchApi(`/admin/accesos/sospechosos${qs({ dias, umbral, ventanaMinutos })}`),

  listar: async ({ usuarioId, contenidoId, desde, hasta, pagina = 1, tamano = 50 } = {}) =>
    fetchApi(`/admin/accesos${qs({ usuarioId, contenidoId, desde, hasta, pagina, tamano })}`),

  decodeWatermark: async (texto) =>
    fetchApi('/admin/accesos/decode-watermark', {
      method: 'POST',
      body: JSON.stringify({ texto }),
    }),
};

export default {
  auth: authService,
  me: meService,
  instituciones: institucionService,
  pei: peiService,
  periodos: periodoService,
  matriculasInstitucionales: matriculaInstitucionalService,
  pensiones: pensionService,
  catalogos: catalogoService,
  geografia: geografiaService,
  cursos: cursoService,
  aprendizaje: aprendizajeService,
  evaluaciones: evaluacionService,
  entregas: entregaService,
  rubricas: rubricaService,
  sesionesOnline: sesionOnlineService,
  sesionesGrabadas: sesionGrabadaService,
  chat: chatService,
  foros: foroService,
  admin: adminService,
  storage: storageService,
};

// ═══════════════════════════════════════════════════════════════════════
//  Fase 5 Redeprog SaaS: organizacionService + authService local
// ═══════════════════════════════════════════════════════════════════════

/**
 * organizacionService — consulta la Organizacion (tenant) activa del
 * backend. Se llama al montar la app (ThemeContext) para autoconfigurar
 * branding (logo, colores, tipografía). En modo Fenagro devuelve la seed
 * FENAGRO; en modo Redeprog la que corresponda al subdominio actual.
 */
export const organizacionService = {
  getActual: async () => {
    // Endpoint público, sin auth. En modo Redeprog el backend resuelve
    // el tenant por el host (subdominio o dominio propio).
    const resp = await fetch(`${API_BASE_URL}/organizacion/actual`);
    if (!resp.ok) return null;
    return await resp.json();
  },
};

/**
 * organizacionesAdminService — CRUD de Organizaciones para el SuperAdmin
 * de Redeprog (Fase 6). Todos los endpoints requieren rol Administrador
 * global (organizacion_id claim vacio + rol Administrador). En modo Fenagro
 * devuelven 404 por el filter RequireSuperAdminRedeprogFilter del backend.
 */
export const organizacionesAdminService = {
  getAll: async (soloActivas = false) =>
    fetchApi(`/admin/organizaciones?soloActivas=${soloActivas}`),

  getById: async (id) => fetchApi(`/admin/organizaciones/${id}`),

  create: async (data) => fetchApi('/admin/organizaciones', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: async (id, data) => fetchApi(`/admin/organizaciones/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  toggleActiva: async (id) => fetchApi(`/admin/organizaciones/${id}/toggle-activa`, {
    method: 'PATCH',
  }),
};

// ═══════════════════════════════════════════════════════════════════════
// CERTIFICADOS ACADÉMICOS
// ═══════════════════════════════════════════════════════════════════════
export const certificadoService = {
  // Público — verificación por código del QR (sin auth)
  // El endpoint es [AllowAnonymous]; si no hay token, fetchApi no envía Authorization y pasa.
  validar: async (codigo) => fetchApi(`/certificados/validar/${codigo}`),

  // Estudiante
  getMios: async () => fetchApi('/certificados/mios'),

  // Admin — certificados emitidos
  listarAdmin: async ({ estado, cursoId, busqueda, pagina = 1, tamano = 25 } = {}) => {
    const qs = new URLSearchParams();
    if (estado)   qs.append('estado', estado);
    if (cursoId)  qs.append('cursoId', cursoId);
    if (busqueda) qs.append('busqueda', busqueda);
    qs.append('pagina', pagina);
    qs.append('tamano', tamano);
    return fetchApi(`/admin/certificados?${qs}`);
  },
  revocar: async (id, motivo) => fetchApi(`/admin/certificados/${id}/revocar`, {
    method: 'POST', body: JSON.stringify({ motivo }),
  }),
  aprobar: async (id) => fetchApi(`/admin/certificados/${id}/aprobar`, { method: 'POST' }),
  emitirManual: async (matriculaId) => fetchApi(`/admin/certificados/emitir-manual/${matriculaId}`, { method: 'POST' }),
  verificacionesAdmin: async (id) => fetchApi(`/admin/certificados/${id}/verificaciones`),

  // Admin — plantillas
  listarPlantillas: async (institucionId) => {
    const qs = institucionId ? `?institucionId=${institucionId}` : '';
    return fetchApi(`/admin/certificados/plantillas${qs}`);
  },
  crearPlantilla: async (data) => fetchApi('/admin/certificados/plantillas', {
    method: 'POST', body: JSON.stringify(data),
  }),
  actualizarPlantilla: async (id, data) => fetchApi(`/admin/certificados/plantillas/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  eliminarPlantilla: async (id) => fetchApi(`/admin/certificados/plantillas/${id}`, { method: 'DELETE' }),
  subirImagenMaterial: async (contenidoId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/materiales/${contenidoId}/imagen`, { method: 'POST', body: fd });
  },
  subirLogoPlantilla: async (plantillaId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/certificados/plantillas/${plantillaId}/logo`, { method: 'POST', body: fd });
  },
  subirFirmaPlantilla: async (plantillaId, file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    return fetchApi(`/storage/certificados/plantillas/${plantillaId}/firma`, { method: 'POST', body: fd });
  },

  // Admin — configuración runtime (dominio del QR, umbrales)
  getConfiguracion: async () => fetchApi('/admin/certificados/configuracion'),
};

// ─── Admin: categorías de cursos ────────────────────────────────────────
// CRUD completo contra /admin/categorias. Solo Administrador global
// puede consumir estos endpoints; el filtro lo exige en backend.
// El getAll aquí devuelve TODAS las categorias (incluye inactivas),
// a diferencia de catalogoService.getCategorias() que es la lectura
// publica y solo trae las activas.
export const categoriaAdminService = {
  getAll: async () => {
    if (USE_MOCK) return [];
    return await fetchApi('/admin/categorias');
  },
  create: async (data) => fetchApi('/admin/categorias', {
    method: 'POST',
    body: JSON.stringify({
      nombre: data.nombre, descripcion: data.descripcion,
      icono: data.icono, color: data.color,
      orden: data.orden || 0, activa: data.activa !== false,
    }),
  }),
  update: async (id, data) => fetchApi(`/admin/categorias/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      nombre: data.nombre, descripcion: data.descripcion,
      icono: data.icono, color: data.color,
      orden: data.orden || 0, activa: data.activa !== false,
    }),
  }),
  reordenar: async (items) => fetchApi('/admin/categorias/reordenar', {
    method: 'PUT', body: JSON.stringify({ items }),
  }),
  // Soft delete por default
  eliminar: async (id) => fetchApi(`/admin/categorias/${id}`, { method: 'DELETE' }),
  // Hard delete solo si no hay cursos vinculados
  eliminarDefinitivo: async (id) => fetchApi(`/admin/categorias/${id}/hard`, { method: 'DELETE' }),
};

// Agrego actualizarConfig al certificadoService (se quedo huerfano al
// insertar el nuevo service encima — lo recupero aqui).
certificadoService.actualizarConfig = async (clave, valor) => fetchApi(
  `/admin/certificados/configuracion/${clave}`,
  { method: 'PUT', body: JSON.stringify({ valor }) }
);

